import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import * as DisabledReport from 'reports/components/disabled';
import * as Common from 'common/components';
import { saveAsCSV } from 'lib/util';
import FileSaver from 'file-saver';
import moment from 'moment';

import {
  getDisabledReportXLSX,
  getDisabledReportCSV,
  getDisabledReportPDF,
} from '../actions/disabled_report_actions';

import { getCompany, getSubcompany, getPermissions } from 'auth/selectors';

import {
  getPagination,
  getIsLoading,
  getVisibleAssets,
  getFilters,
  getNeedFetch,
} from 'selectors/reports_depreciation';
import {
  getVisibleAccountsParent,
  getVisibleCostCenters,
  getVisibleEmployees,
  getVisibleStatus,
} from 'selectors/lists';

import { generateDisabledPDF } from '../../../../../lib/pdfs/DisabledPDF';

class DisabledReportView extends Component {
  static propTypes = {
    company: PropTypes.object.isRequired,
    subcompany: PropTypes.object.isRequired,
    count: PropTypes.number.isRequired,
    children: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.shape({
      fulltext: PropTypes.string.isRequired,
      depreciationType: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
    }).isRequired,
    pagination: PropTypes.shape({
      page: PropTypes.number.isRequired,
      limit: PropTypes.number.isRequired,
      totalPages: PropTypes.number.isRequired,
      orderBy: PropTypes.string.isRequired,
      order: PropTypes.string.isRequired,
    }).isRequired,
    isLoading: PropTypes.bool.isRequired,
    getDepreciationCSV: PropTypes.func.isRequired,
    disabledReportXLSX: PropTypes.func.isRequired,
    getDisabledReportPDF: PropTypes.func.isRequired,
    handleFiltersChange: PropTypes.func.isRequired,
    handleFiltersSubmit: PropTypes.func.isRequired,
    handleLimitChange: PropTypes.func.isRequired,
    handleOrderChange: PropTypes.func.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  };

  /**
   * @author Jorge Casas
   *
   * @function handleExportXLSX
   *
   * @description This function is used to handle the export of the report in XLSX format
   *
   * @param {object} e
   * @returns {object}
   */
  handleExportXLSX = (e) => {
    const { company, subcompany, filters } = this.props;

    this.props
      .getDisabledReportXLSX(company.id, subcompany.id, filters)
      .then((response) => {
        if (
          response &&
          response.value &&
          response.value.data &&
          response.value.data.type === 'Buffer' &&
          Array.isArray(response.value.data.data) &&
          response.value.data.data.length > 0
        ) {
          const dataArray = new Uint8Array(response.value.data.data);
          const blob = new Blob([dataArray], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          FileSaver.saveAs(blob, `disabled-${moment().format('DDMMYYYY')}.xlsx`);
        } else {
          console.error('La respuesta no contiene datos válidos:', response);
        }
      })
      .catch((error) => {
        console.error('Error al exportar XLSX:', error);
      });
  };

  handleExport = (e) => {
    const { company, subcompany, filters, getDisabledReportCSV } = this.props;

    getDisabledReportCSV(company.id, subcompany.id, filters)
      .then(({ value: { data } }) => saveAsCSV(`disabled`, filters.endDate, data))
      .catch((e) => {});
  };

  handleExportPDF = (e) => {
    const { company, subcompany } = this.props;

    this.props
      .getDisabledReportPDF(company.id, subcompany.id, this.props.filters)
      .then(({ value: { data } }) =>
        generateDisabledPDF(
          this.props.subcompany,
          this.props.filters.endDate,
          this.props.filters.depreciationType,
          data
        )
      )
      .catch((e) => {
        console.log(e);
      });
  };

  render = () => (
    <Common.Wrapper fluid={true}>
      <div className="col-12">
        <Common.Portlet
          title={this.props.t('reports.title') + ' > ' + this.props.t('reports.disabled.subtitle')}
          icon={'table'}
          button={
            <div className="btn-group pull-right">
              <Common.Dropdown>
                <Common.DropdownElement onClick={this.handleExportXLSX}>
                  <i className="fa fa-file-excel-o mr-1" />
                  {'  '}
                  {this.props.t('actions.exportXLSX')}
                </Common.DropdownElement>
                <Common.DropdownElement onClick={this.handleExport}>
                  <i className="fa fa-table mr-1" />
                  {'  '}
                  {this.props.t('actions.exportCSV')}
                </Common.DropdownElement>
                <Common.DropdownElement onClick={this.handleExportPDF}>
                  <i className="fa fa-archive mr-1" />
                  {'  '}
                  {this.props.t('actions.exportPDF')}
                </Common.DropdownElement>
              </Common.Dropdown>
            </div>
          }
        >
          <DisabledReport.Filters
            accounts={this.props.accounts}
            employees={this.props.employees}
            costCenters={this.props.costCenters}
            status={this.props.status}
            values={this.props.filters}
            handleValueChange={this.props.handleFiltersChange}
            handleSubmit={this.props.handleFiltersSubmit}
          />
          <div className="mb-2" style={{ position: 'relative' }}>
            <DisabledReport.Table
              company={this.props.company}
              subcompany={this.props.subcompany}
              permissions={this.props.permissions}
              count={this.props.count}
              orderBy={this.props.pagination.orderBy}
              order={this.props.pagination.order}
              handleOrderChange={this.props.handleOrderChange}
            >
              {this.props.children}
            </DisabledReport.Table>
            <Common.LoaderOverlay show={this.props.isLoading} />
          </div>
          <Common.Pagination
            {...this.props.pagination}
            handleLimitChange={this.props.handleLimitChange}
            handlePageChange={this.props.handlePageChange}
          />
        </Common.Portlet>
      </div>
    </Common.Wrapper>
  );
}

const mapStateToProps = (state) => ({
  company: getCompany(state),
  subcompany: getSubcompany(state),
  isLoading: getIsLoading(state),
  pagination: getPagination(state),
  permissions: getPermissions(state),
  assets: getVisibleAssets(state),
  filters: getFilters(state),
  needFetch: getNeedFetch(state),
  accounts: getVisibleAccountsParent(state),
  costCenters: getVisibleCostCenters(state),
  employees: getVisibleEmployees(state),
  status: getVisibleStatus(state),
});

const mapDispatchToProps = {
  getDisabledReportCSV,
  getDisabledReportXLSX,
  getDisabledReportPDF,
};

export default connect(mapStateToProps, mapDispatchToProps)(translate()(DisabledReportView));



// WEBPACK FOOTER //
// src/modules/secured/modules/reports/components/DisabledReportView.js